// Phase SE-8 — Read-only hook over public.slip_extractions for the
// signed-in user. Mirrors useAutopsyHistory pattern but pulls
// extraction-specific fields (user_prompt, filter_plan, scored_legs,
// selected_leg_indices, aggregates).
//
// Pulls the most recent 100 rows; pagination deferred per the SA-8
// precedent.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface ExtractionHistoryRow {
  extraction_id: string;
  user_id: string;
  input_mode: "paste" | "upload";
  slip_text: string | null;
  slip_image_url: string | null;
  slip_image_mime: string | null;
  slip_image_size_bytes: number | null;
  user_prompt: string;
  extracted_legs: unknown[]; // typed loosely; ResultPanel knows the shape
  filter_plan: unknown;
  plan_summary: string | null;
  scored_legs: unknown[];
  selected_leg_indices: number[];
  combined_odds_kept: number | null;
  combined_odds_dropped: number | null;
  hit_probability_kept: number | null;
  expected_value_kept: number | null;
  status: "processing" | "complete" | "queued" | "declined" | "failed";
  declined_reason: string | null;
  created_at: string;
  completed_at: string | null;
}

const FETCH_LIMIT = 100;

export interface UseExtractionHistoryState {
  rows: ExtractionHistoryRow[];
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useExtractionHistory(): UseExtractionHistoryState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  // Key off the STABLE user id, not the user object. Supabase swaps the
  // session/user object for a new identity on every window-focus token
  // refresh; depending on the object re-created refetch on each focus,
  // re-fired the effect, and flipped `loading` back to true — which, when
  // a query was queued behind an in-flight token refresh, never settled
  // (history stuck on "Loading…"). The id is unchanged across refreshes.
  const userId = user?.id ?? null;
  const [rows, setRows] = useState<ExtractionHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Only the most recent fetch is allowed to write state.
  const seqRef = useRef(0);

  const refetch = useCallback(async (): Promise<void> => {
    const seq = ++seqRef.current;
    const settle = (r: ExtractionHistoryRow[]): void => {
      if (seqRef.current !== seq) return; // a newer fetch superseded this one
      setRows(r);
      setLoading(false);
    };
    if (!userId) {
      settle([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("slip_extractions" as never)
        .select(
          // eslint-disable-next-line max-len
          "extraction_id, user_id, input_mode, slip_text, slip_image_url, slip_image_mime, slip_image_size_bytes, user_prompt, extracted_legs, filter_plan, plan_summary, scored_legs, selected_leg_indices, combined_odds_kept, combined_odds_dropped, hit_probability_kept, expected_value_kept, status, declined_reason, created_at, completed_at",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(FETCH_LIMIT);

      if (error) {
        // N01 spirit: soft-fail to empty.
        // eslint-disable-next-line no-console
        console.warn("[useExtractionHistory] fetch failed:", error.message);
        settle([]);
        return;
      }
      settle((data ?? []) as unknown as ExtractionHistoryRow[]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "[useExtractionHistory] threw:",
        e instanceof Error ? e.message : String(e),
      );
      settle([]);
    }
  }, [supabase, userId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseExtractionHistoryState>(
    () => ({ rows, loading, refetch }),
    [rows, loading, refetch],
  );
}
